import React, { createContext, useContext } from 'react'
import moment from 'moment'

import { useServiceScheduleContext, actionTypes } from './serviceScheduleContext'
import { getNames } from '../services/professionalService'
import { getBestPromotion } from '../services/promotionService'

import { DEFAULT_ERROR, INITIAL_SCHEDULE_BODY } from '../utils/schedulingConstants'
import { focus, calculateTotalPrice } from '../utils'

export const dateTimeContext = createContext()

export function DateTimeContextProvider ({ children }) {
  const { state, dispatch, functions: schedulingFunctions } = useServiceScheduleContext()
  const {
    checkReservedDate,
    findPartnerScheduleAndCampaignAviability
  } = schedulingFunctions

  const getAvailableProfessionalsByDate = ({ selectDay, selectTime }) => {
    const { availableSlots } = state
    const availableProfessionals = []

    Object.entries(availableSlots).forEach(([professional, dates]) => {
      if (professional) {
        // Converte dia em string para numero utilizando o unary operator (+)
        const day = +selectDay.split('-').pop()
        const available = dates[day]?.includes(selectTime)
        // Verifica se a data e hora selecionadas para cada profissional não está ocupada em outro agendamento em andamento (múltiplo agendamento)
        const notReserved = checkReservedDate({ professional, selectDay, selectTime })

        if (available && notReserved) {
          availableProfessionals.push(professional)
        }
      }
    })

    return availableProfessionals
  }

  const handleSelectTime = async (time, props) => {
    const { context } = props

    const {
      error,
      formList,
      petsData,
      invertStep,
      scheduleBody,
      partnerSelected,
      selectedPetSchedule,
      professionalSelected
    } = state

    const { selectDay } = scheduleBody

    context.showLoader()

    const newState = {
      ...state
    }

    const newError = Object.assign({}, error)

    newError.selectTime = time ? undefined : DEFAULT_ERROR.selectTime

    if (invertStep) {
      const professionalIds = getAvailableProfessionalsByDate({ selectDay, selectTime: time })

      if (!professionalIds.length) {
        const unavailablePetSchedule = petsData.find(
          (pet) => selectedPetSchedule && pet.id === selectedPetSchedule.id
        )

        if (unavailablePetSchedule) {
          const newError = {
            ...error,
            unavailabilityPets: [
              ...error.unavailabilityPets,
              {
                ...unavailablePetSchedule
              }
            ]
          }

          newState.error = newError
          newState.updatedProfessionals = true
          newState.activeStep = 5
        }
      }

      const professionalList = await getNames(professionalIds)

      const partnerEnablePayment = partnerSelected?.enabledPayment === true
      const partnerOpenSeller = partnerSelected?.openSeller === true
      const professionalEnablePayment =
        professionalSelected?.enabledPayment === true

      // Se já foi adicionado um agendamento para um pet, define se haverá pagamento ou não de acordo com o primeiro agendamento,
      // caso contrário, verifica se o pagamento está habilitado para a loja e profissional selecionados no agendamento atual
      const havePayment = formList.length
        ? formList[0].data.havePayment
        : partnerEnablePayment &&
        (professionalEnablePayment || partnerOpenSeller)

      const professionalData = professionalList
        .map(({ id, name, photo, enabledPayment, ratingAverage }) => ({ id, name, photo, enabledPayment: enabledPayment || false, ratingAverage }))
        .filter(({ enabledPayment }) => !formList.length || (havePayment && enabledPayment === havePayment) || !havePayment)

      newState.professionalData = professionalData
    }

    newState.scheduleBody.selectTime = time

    newState.error = newError

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: newState
    })

    context.hideLoader()
  }

  const handleSelectDay = async (date, ref, props) => {
    const { context } = props

    context.showLoader()

    const {
      scheduleBody,
      error,
      partnersData,
      availableSlots,
      campaigns,
      schedulingCoupon,
      pricesData
    } = state

    const price = calculateTotalPrice(pricesData)

    const {
      partner_id: partnerId,
      professional_id: professionalId,
      service_id: serviceId
    } = scheduleBody

    const newError = Object.assign({}, error)

    const day = moment(date).date()
    const selectDay = moment(date).format('YYYY-MM-DD')

    newError.selectDay = day ? undefined : DEFAULT_ERROR.selectDay

    const selectedPartner = partnersData.find(({ id }) => id === partnerId)

    let availableTimes = availableSlots[professionalId][day]

    // Desabilita os horários já selecionados em um agendamento ainda não finalizado para um ou mais pets
    availableTimes = availableTimes?.filter(time => {
      // Verifica se a data e hora a ser disponibilizada para seleção não está ocupada por um agendamento feito para outro pet e mesmo profissional,
      // ou, se não houver preferência de profissional valida também se há pelo menos 1 profissional disponível na data e hora e que não esteja reservada em outro agendamento em andamento (múltiplo agendamento)
      if (professionalId) {
        return checkReservedDate({ professional: professionalId, selectDay, selectTime: time })
      }

      return getAvailableProfessionalsByDate({ selectDay, selectTime: time }).length
    })

    let startTime = moment('08:00', 'HH:mm')
    const endTime = moment('22:00', 'HH:mm')

    const intervalTime = selectedPartner && selectedPartner.schedulingInterval ? selectedPartner.schedulingInterval : 15
    const times = []

    while (startTime <= endTime) {
      times.push(startTime.format('HH:mm'))
      startTime = startTime.add(intervalTime, 'minutes')
    }

    const activeCampaign = campaigns?.find((campaign) => (moment(selectDay).isBetween(
      moment(campaign.start),
      moment(campaign.end),
      null,
      '[]'
    )) && campaign.status === 'ACTIVE' &&
      campaign.partners.includes(partnerId) && campaign.services.includes(serviceId))

    const isAppliedSchedulingCoupon = async () => {
      if (activeCampaign && schedulingCoupon) {
        const bestDiscount = await getBestPromotion(activeCampaign.id, schedulingCoupon.id, price)
        if (bestDiscount.promotion === 'campaign') return false
      }

      if (!moment(selectDay).isBetween(schedulingCoupon?.start, schedulingCoupon?.end)) return false
      if (!Array.isArray(schedulingCoupon?.weekdays)) return false

      return schedulingCoupon.weekdays.includes(moment(selectDay).weekday())
    }

    const hasSchedulingCoupon = await isAppliedSchedulingCoupon()

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        timeData: availableTimes,
        times,
        error: {
          ...error,
          ...newError
        },
        scheduleBody: {
          ...scheduleBody,
          selectDay,
          selectTime: INITIAL_SCHEDULE_BODY.selectTime
        },
        activeCampaign: activeCampaign,
        schedulingCoupon: hasSchedulingCoupon ? schedulingCoupon : undefined
      }
    })
    focus(ref, 'smooth', true)
    context.hideLoader()
  }

  const handleCalendarNavigation = async (newDate, props) => {
    const { scheduleBody, activeCampaign, selectedPurchasedPackage } = state
    const { context } = props

    // Bloqueia navegação para meses além da expiração do pacote
    if (scheduleBody.isSchedulingFromPackage && selectedPurchasedPackage?.expiresAt) {
      const targetMonth = moment(newDate).startOf('month')
      const expiryMonth = moment(selectedPurchasedPackage.expiresAt).startOf('month')
      if (targetMonth.isAfter(expiryMonth)) {
        return
      }
    }

    const {
      partner_id: partnerId
    } = scheduleBody

    context.showLoader()

    const selectDay = moment(newDate).format('YYYY-MM-DD')

    let newState = await findPartnerScheduleAndCampaignAviability(partnerId, selectDay)

    newState = {
      ...newState,
      scheduleBody: {
        ...scheduleBody,
        selectDay: undefined,
        selectTime: undefined
      },
      calendarMonth: moment(selectDay, 'YYYY-MM-DD').month() + 1,
      calendarYear: moment(selectDay, 'YYYY-MM-DD').year(),
      activeCampaign: scheduleBody.selectDay && activeCampaign ? activeCampaign : newState.activeCampaign
    }

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: newState
    })
    context.hideLoader()
  }

  const functions = {
    handleSelectDay,
    handleSelectTime,
    handleCalendarNavigation
  }

  return (
    <dateTimeContext.Provider value={{ functions }}>
      {children}
    </dateTimeContext.Provider>
  )
}

export function useDateTimeContext () {
  const context = useContext(dateTimeContext)
  if (!context) {
    throw new Error('useDateTimeContextdeve ser usado dentro de um Provider!')
  }

  const { functions } = context
  return { functions }
}
